import { useEffect, useState } from 'react'
import { api } from '../lib/api'
import { formatShortDate } from '../lib/format'

// The fancy-notes dropdown, and the hook that loads what it lists. Step one of
// New space ("Start from a fancy-note (optional)") and the same control beside
// the brief in Settings both use it: picking a note hands the caller the note,
// and the caller decides what to do with it — fill the brief, suggest a title.
//
// The list is loaded once per mount. A 409 means the integration is off on
// this server, and the callers hide the whole step for it rather than showing
// a dropdown that can never fill; any other failure keeps the step visible with
// the error under it, so the admin knows the notes exist and just could not be
// fetched right now.

export function useFancyNotes() {
  const [state, setState] = useState({ status: 'loading', notes: [], error: null })

  useEffect(() => {
    let cancelled = false
    api('/api/v1/fancy-notes')
      .then((notes) => {
        if (!cancelled) setState({ status: 'ready', notes, error: null })
      })
      .catch((err) => {
        if (cancelled) return
        if (err.status === 409) setState({ status: 'off', notes: [], error: null })
        else
          setState({
            status: 'error',
            notes: [],
            error: err.detail || 'Could not load the fancy-notes.',
          })
      })
    return () => {
      cancelled = true
    }
  }, [])

  return state
}

// "Title · 14 Jul 2026 · owner@capston.ca" — what a row of the dropdown says.
export function noteLabel(note) {
  const when = note.date ? formatShortDate(note.date) : null
  return [note.title, when, note.owner].filter(Boolean).join(' · ')
}

export default function FancyNotePicker({
  id,
  label = 'Fancy-note',
  fancy,
  value,
  onPick,
  help,
  disabled = false,
}) {
  const { status, notes, error } = fancy
  const ready = status === 'ready'

  function handleChange(event) {
    const picked = notes.find((note) => note.id === event.target.value) || null
    onPick(picked)
  }

  return (
    <div className="field">
      <label className="field__label" htmlFor={id}>
        {label}
      </label>
      <select
        id={id}
        className="field__input"
        value={ready ? value : ''}
        onChange={handleChange}
        disabled={disabled || !ready}
        aria-busy={status === 'loading'}
      >
        {status === 'loading' && <option value="">Loading fancy-notes…</option>}
        {status === 'error' && <option value="">Fancy-notes unavailable</option>}
        {ready && (
          <option value="">
            {notes.length > 0 ? 'None — start blank' : 'No public or published notes yet'}
          </option>
        )}
        {ready &&
          notes.map((note) => (
            <option key={note.id} value={note.id}>
              {noteLabel(note)}
            </option>
          ))}
      </select>
      {status === 'error' ? (
        <span className="field__error" role="alert">
          {error}
        </span>
      ) : (
        help && <span className="field__help">{help}</span>
      )}
    </div>
  )
}
